import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAdmin } from '@vns/core';
import { ENTRY_SOURCES, looseFrom, rel, type Row } from './entry-sources';
import { useSession } from './session';

/**
 * The admin's review queue.
 *
 * Nothing a site engineer submits counts until it passes through here. The
 * database enforces that — every ledger, total and report filters on
 * approval_status = 'approved' — so this screen is the only way a field entry
 * becomes real.
 */

export interface PendingEntry {
  id: string;
  table: string;
  kind: string;
  date: string;
  summary: string;
  detail: Array<[string, string]>;
  projectName: string;
  submittedBy: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewNote: string | null;
  reviewedAt: string | null;
}

export function useApprovals(status: 'pending' | 'approved' | 'rejected') {
  return useQuery({
    queryKey: ['approvals', status],
    // Pending work should feel live when two people are looking at it.
    refetchInterval: status === 'pending' ? 30_000 : false,
    queryFn: async (): Promise<PendingEntry[]> => {
      const results = await Promise.all(
        ENTRY_SOURCES.map(async (src) => {
          let q = looseFrom(src.table)
            .select(src.selectWithSubmitter)
            .eq('approval_status', status)
            .is('deleted_at', null)
            .order(src.dateCol, { ascending: false })
            .limit(100);
          if (src.filter) q = src.filter(q);

          const { data, error } = await q;
          if (error) return [];

          return (data ?? []).map((raw): PendingEntry => {
            const r = raw as Row;
            return {
              id: String(r.id),
              table: src.table,
              kind: src.kind,
              date: String(r[src.dateCol] ?? ''),
              summary: src.describe(r),
              detail: src.detail(r),
              projectName: rel(r.projects),
              submittedBy: rel(r.submitted_by_profile, 'full_name'),
              submittedAt: String(r.submitted_at ?? ''),
              status: r.approval_status as PendingEntry['status'],
              reviewNote: (r.review_note as string) ?? null,
              reviewedAt: (r.reviewed_at as string) ?? null,
            };
          });
        }),
      );

      return results
        .flat()
        .sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : a.submittedAt > b.submittedAt ? -1 : 0));
    },
  });
}

/** Count for the sidebar badge — head-only, so no rows travel. */
export function usePendingCount() {
  const actor = useSession((s) => s.actor);
  return useQuery({
    queryKey: ['approvals', 'count'],
    enabled: !!actor && isAdmin(actor),
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const counts = await Promise.all(
        ENTRY_SOURCES.map((src) => {
          const q = looseFrom(src.table)
            .select('id')
            .eq('approval_status', 'pending')
            .is('deleted_at', null);
          // Must match useApprovals exactly, or the badge promises work the
          // inbox does not show.
          return src.filter ? src.filter(q) : q;
        }),
      );
      return counts.reduce((n, c) => n + (c.error ? 0 : (c.data ?? []).length), 0);
    },
  });
}

/**
 * Approving stamps the reviewer via the stamp_submission trigger and, because
 * every ledger filters on approval_status, immediately makes the entry count
 * everywhere at once. There is no second write to keep in step.
 */
export function useReviewEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ table, id, decision, note }: {
      table: string; id: string;
      decision: 'approved' | 'rejected';
      note?: string;
    }) => {
      const { error } = await looseFrom(table)
        .update({
          approval_status: decision,
          review_note: note?.trim() || null,
        })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      // A decision changes totals everywhere, so refresh broadly rather than
      // trying to predict which screens are affected.
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['client'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      void qc.invalidateQueries({ queryKey: ['bills'] });
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['supplier'] });
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor'] });
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
      void qc.invalidateQueries({ queryKey: ['staff-dashboard'] });
      void qc.invalidateQueries({ queryKey: ['company-metrics'] });
    },
  });
}
